import React, { useEffect, useMemo, useState } from "react";
import { sunboundPayProxy } from '@proxies';
import { useSpinnerContext, useSessionContext } from '@contexts';
import classNames from "classnames";
import { useForm, validators } from "@hooks";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import PropTypes from 'prop-types';
import { StatusCodes } from "http-status-codes";

const AddForm = ({ addPayor, cancel }) => {

    const { setSpinnerShow } = useSpinnerContext();

    const validations = {
        email: (val) => validators.isValidEmail(val) || { email: `Email is not valid` },
        fullName: (val) => validators.hasValue(val) || { fullName: `Full name is required` }
    };

    const handleSubmit = async (values) => {

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.getPayorCheck(values.email);
            setCustomError(`email`, `Email is already registered`);
            return;
        } catch (error) {
            if (error.response?.status !== StatusCodes.NOT_FOUND) {
                console.error(error);
                return;
            }
        } finally {
            setSpinnerShow(false);
        }
        addPayor(values);
    };

    const { values, errors, changeHandler, submitHandler, setCustomError } = useForm({}, {}, validations, handleSubmit);

    return (
        <form onSubmit={(e) => submitHandler(e)} className="w-full">
            <div className="content-box border-2 shadow-sb bg-white gap-y-4">
                <h1 className="text-center text-gray-600 text-2xl">Create New Payor</h1>
                <div className="w-full">
                    <label htmlFor="email" className="font-semibold text-gray-700">Email</label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                        <input
                            type="text"
                            name="email"
                            id="email"
                            className={classNames(`block w-full rounded-md shadow-md focus:outline-none border-gray-300 text-gray-600 disabled:bg-gray-200`, { "input-error": errors.email })}
                            value={values.email}
                            onChange={changeHandler} />
                        {
                            errors.email &&
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ExclamationCircleIcon className="fill-red-500 h-6" />
                            </div>
                        }
                    </div>
                    {
                        errors.email &&
                        <p className="mt-2 text-sm text-red-600" id="email-error">{errors.email}</p>
                    }
                </div>
                <div className="w-full">
                    <label htmlFor="fullName" className="font-semibold text-gray-700">Full Name</label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                        <input
                            type="text"
                            name="fullName"
                            id="fullName"
                            className={classNames(`block w-full rounded-md shadow-md focus:outline-none border-gray-300 text-gray-600`, { "input-error": errors.fullName })}
                            value={values.fullName}
                            onChange={changeHandler} />
                        {
                            errors.fullName &&
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ExclamationCircleIcon className="fill-red-500 h-6" />
                            </div>
                        }
                    </div>
                    {
                        errors.fullName &&
                        <p className="mt-2 text-sm text-red-600" id="email-error">{errors.fullName}</p>
                    }
                </div>
                <div className="flex flex-row w-full gap-x-5 justify-center">
                    <button className="btn btn-outline-primary w-1/2 rounded-full self-center p-3 border-2 my-3" type="button" onClick={cancel}>Cancel</button>
                    <button className="btn btn-outline-primary w-1/2 rounded-full self-center p-3 border-2 my-3" type="submit">Add</button>
                </div>
            </div>
        </form>
    );
};

AddForm.propTypes = {
    addPayor: PropTypes.func.isRequired,
    cancel: PropTypes.func.isRequired
};

const ManagePayors = () => {

    const Filters = {
        payors: 0,
        deleted: 1
    };

    const { setSpinnerShow } = useSpinnerContext();
    const { session } = useSessionContext();
    const [accountPayors, setAccountPayors] = useState([]);
    const [filter, setFilter] = useState(Filters.payors);
    const [adding, setAdding] = useState(false);
    const { idAccount } = session;

    const payors = useMemo(
        () => accountPayors.filter(ap => ap.isActive),
        [accountPayors]
    );

    const deletedPayors = useMemo(
        () => accountPayors.filter(ap => !ap.isActive),
        [accountPayors]
    );

    useEffect(() => {
        (async () => {
            setSpinnerShow(true);
            try {
                const ap = await sunboundPayProxy.getAccountPayors(idAccount);
                setAccountPayors(ap);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);

    let selected, text;
    switch (filter) {
        case Filters.deleted:
            selected = deletedPayors;
            text = `These are the payors you have deleted. They will not available for you to invite to help pay senior care bills, unless you choose to “restore” them`;
            break;
        default:
            selected = payors;
            text = `These are the payors who you may choose to ask for help paying your senior care bills.  Their specific contribution amounts and dates can be managed in your Subscription Settings, or when you are enrolling an invoice`;
            break;
    }

    const deletePayor = async (idPayor) => {
        setSpinnerShow(true);
        try {

            await sunboundPayProxy.disablePayor(idAccount, idPayor);
            const ap = await sunboundPayProxy.getAccountPayors(idAccount);
            setAccountPayors(ap);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const enablePayor = async (idPayor) => {

        setSpinnerShow(true);
        try {
            await sunboundPayProxy.enablePayor(idAccount, idPayor);
            const ap = await sunboundPayProxy.getAccountPayors(idAccount);
            setAccountPayors(ap);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const changeFilter = (filter) => {
        setFilter(filter);
        const newAccountPayors = [...accountPayors];
        const removeNotes = newAccountPayors.filter(p => !p.removed);
        setAccountPayors(removeNotes);
    };

    const addPayor = async (values) => {
        setSpinnerShow(true);
        try {
            await sunboundPayProxy.createPayor(idAccount, { ...values });
            const ap = await sunboundPayProxy.getAccountPayors(idAccount);
            setAccountPayors(ap);
            setAdding(false);
        } catch (error) {
            console.error(error);
        }
        setSpinnerShow(false);
    };

    const cancel = () => setAdding(false);

    return (
        <div className="page-content-wrapper">
            <div className="flex flex-col w-full p-5 rounded-xl gap-y-7 items-start">
                <div className="flex flex-col gap-y-3">
                    <span className="font-bold text-2xl">Manage Payors</span>
                    <span className="text-gray-500">
                        Every month that you’re enrolled, Sunbound will request payments from the “active” payors according to the amounts and dates below
                    </span>
                </div>
                <div className="w-full rounded-xl bg-sb-frame px-5 py-1 border-gray-400 border-2">
                    <nav className="flex justify-between text-lg font-normal text-gray-500" aria-label="Tabs">
                        <button
                            type="button"
                            onClick={() => changeFilter(Filters.payors)}
                            className={classNames(
                                { "bg-blue-text text-white": filter === Filters.payors },
                                { "hover:text-gray-700": filter !== Filters.payors },
                                `rounded-xl px-4 py-1 w-1/3`
                            )}>Payors</button>
                        <button
                            type="button"
                            onClick={() => changeFilter(Filters.deleted)} className={classNames(
                                { "bg-blue-text text-white": filter === Filters.deleted },
                                { "hover:text-gray-700": filter !== Filters.deleted },
                                `rounded-xl px-4 py-1 w-1/3`
                            )}>Deleted</button>
                    </nav>
                </div>
                <div className="flex flex-col bg-sb-frame shadow-sb rounded-lg w-full gap-y-4 p-4">
                    <div>
                        <span className="text-gray-500">{text}</span>
                    </div>
                    {
                        selected.map(ap => {
                            const { idPayor, isGuarantor, isActive, isEnrolled, payor: { email, fullName } } = ap;
                            return (
                                <div key={email} className="bg-sb-bg shadow-sb rounded-lg w-full p-4">
                                    {
                                        <div className="flex flex-row">
                                            <div className="flex flex-col w-2/3 gap-y-1">
                                                <p className="font-bold text-xl">{fullName}
                                                    {
                                                        isEnrolled &&
                                                        <span className="inline-flex items-center rounded-md bg-[#E39E70] ml-3 px-2.5 py-0.5 text-sm font-medium">Enrolled</span>
                                                    }
                                                </p>
                                                <span className="text-base">{email}</span>
                                            </div>
                                            <div className="flex w-1/3 justify-end">
                                                {
                                                    isActive &&
                                                    <button type="button" className="btn-outline-primary h-7 px-3 border-2" disabled={isEnrolled || isGuarantor} onClick={() => deletePayor(idPayor)}>Delete</button>
                                                }
                                                {
                                                    !isActive &&
                                                    <button type="button" className="btn-outline-primary h-7 px-3 border-2" onClick={() => enablePayor(idPayor)}>Restore</button>
                                                }
                                            </div>
                                        </div>}
                                </div>
                            );
                        })
                    }
                </div>
                {
                    adding &&
                    <div className="w-full" >
                        <AddForm addPayor={addPayor} cancel={cancel} />
                    </div>
                }
                {
                    !adding &&
                    <div className="w-full">
                        <button className="btn btn-primary" onClick={() => setAdding(true)}>Create New Payor</button>
                    </div>}

            </div>
        </div>

    );
};

export default ManagePayors;
